import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { X, Camera, Loader2 } from "lucide-react";
import { estafetaAxios } from "@/utils/estafetaAxios";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

export default function DeliveryPhotoModal({ orderId, token, onClose, onSuccess, t }) {
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCapturing(true);
    const reader = new FileReader();
    reader.onload = (ev) => { setPreview(ev.target.result); setCapturing(false); };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      await estafetaAxios(token).post(`${API}/api/v1/deliverers/app/deliver-photo/${orderId}`, { photo: preview });
      toast.success(t("photoSaved"));
      onSuccess();
    } catch { toast.error(t("photoError")); }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end justify-center" data-testid="photo-modal">
      <div className="bg-white w-full max-w-md rounded-t-2xl p-5 space-y-4 animate-in slide-in-from-bottom">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{t("photoTitle")}</h3>
          <button onClick={onClose} className="p-1"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        {preview ? (
          <div className="space-y-3">
            <img src={preview} alt="Entrega" className="w-full h-48 object-cover rounded-lg" />
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => { setPreview(null); fileRef.current?.click(); }} data-testid="photo-retake-btn">{t("retake")}</Button>
              <Button onClick={handleUpload} disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="photo-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("save")}
              </Button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => fileRef.current?.click()}
            className="w-full h-40 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-emerald-400 hover:text-emerald-600 transition-colors"
            data-testid="photo-capture-btn"
          >
            {capturing ? <Loader2 className="w-8 h-8 animate-spin" /> : <Camera className="w-8 h-8" />}
            <span className="text-sm font-medium">{t("takePhoto")}</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleCapture} />
      </div>
    </div>
  );
}
